import { useCallback, useEffect, useRef, useState } from 'react';

import type { AudioVisualizerHookProps } from '.';

export function useAudioVisualizer(audioVisualizerHookProps: AudioVisualizerHookProps): number[] {
  const { isRecording, analyserNode, barCount = 25 } = audioVisualizerHookProps;
  const animationFrameRef = useRef<number>(0);
  const [audioData, setAudioData] = useState<number[]>(new Array(barCount).fill(0));
  const visualizeAudio = useCallback(() => {
    if (!analyserNode) return;

    const bufferLength = analyserNode.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    analyserNode.getByteFrequencyData(dataArray);

    const step: number = Math.floor(bufferLength / barCount);
    const newAudioData: number[] = [];

    for (let i = 0; i < barCount; i++) {
      let sum: number = 0;
      for (let j = 0; j < step; j++) {
        const d = dataArray[i * step + j];
        if (d) {
          sum += d;
        }
      }
      const average: number = sum / step;
      newAudioData.push(Math.floor((average / 255) * 100));
    }

    setAudioData(newAudioData);

    if (isRecording) {
      animationFrameRef.current = requestAnimationFrame(visualizeAudio);
    }
  }, [isRecording, analyserNode, barCount]);

  useEffect(() => {
    if (isRecording && analyserNode) {
      visualizeAudio();
    } else if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isRecording, analyserNode, visualizeAudio]);

  return audioData;
}
